import React, { useEffect, useMemo } from 'react';
import MaterialTable from 'material-table';
import TableIcons from '../components/TableIcons';
import { useDispatch, useSelector } from 'react-redux';
import { api } from 'common';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, MenuItem, Select, Stack, Typography, Chip } from '@mui/material';
import {
  ROLE_DEFINITIONS,
  ROLE_SCOPES,
  getUserRole,
  getUserScope,
  hasPermission,
} from '../common/permissionUtils';

export default function TeamMembers() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { fetchUsersOnce, editUser } = api;
  const usersdata = useSelector((state) => state.usersdata);
  const settings = useSelector((state) => state.settingsdata.settings);
  const auth = useSelector((state) => state.auth);

  const canEdit = hasPermission(auth.profile, settings, 'users', 'update');

  useEffect(() => {
    if (!usersdata.staticusers) {
      dispatch(fetchUsersOnce());
    }
  }, [dispatch, fetchUsersOnce, usersdata.staticusers]);

  const roleLookup = useMemo(() => ROLE_DEFINITIONS.reduce((acc, role) => {
    acc[role.key] = role.label;
    return acc;
  }, {}), []);

  const scopeLookup = useMemo(() => ({
    all: t('scope_all') || 'All data',
    partner: t('scope_partner') || 'Partner data',
    fleet: t('scope_fleet') || 'Fleet data',
    self: t('scope_self') || 'Own data only',
  }), [t]);

  const partnerOptions = useMemo(() => {
    const partners = settings?.partnerPages || [];
    const list = Array.isArray(partners) ? partners : Object.values(partners || {});
    return list
      .map((partner) => ({
        value: partner.slug || partner.partnerSlug || partner.companySlug || '',
        label: partner.name || partner.companyName || partner.slug || '',
      }))
      .filter((item) => item.value);
  }, [settings?.partnerPages]);

  const fleetOptions = useMemo(() => {
    const list = usersdata.staticusers || [];
    return list
      .filter((user) => user.usertype === 'fleetadmin')
      .map((user) => ({
        value: user.id,
        label: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || user.id,
      }))
      .filter((item) => item.value);
  }, [usersdata.staticusers]);

  const data = useMemo(() => {
    const list = usersdata.staticusers || [];
    return list
      .filter((user) => ['admin', 'fleetadmin'].includes(user.usertype) || user.accessRole)
      .map((user) => ({
        ...user,
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        accessRole: user.accessRole || getUserRole(user),
        dataScope: user.dataScope || getUserScope(user, settings) || ROLE_SCOPES[getUserRole(user)] || 'self',
        allowedPartners: Array.isArray(user.allowedPartners) ? user.allowedPartners : [],
        allowedFleets: Array.isArray(user.allowedFleets) ? user.allowedFleets : [],
      }));
  }, [usersdata.staticusers, settings]);

  return (
    <Box>
      <MaterialTable
        icons={TableIcons}
        title={t('team_members') || 'Team members'}
        columns={[
          {
            title: t('name') || 'Name',
            field: 'name',
            editable: 'never',
            render: (row) => (
              <Stack>
                <Typography sx={{ fontWeight: 600 }}>{row.name || row.email}</Typography>
                <Typography variant="caption" sx={{ color: '#6B7280' }}>{row.email}</Typography>
              </Stack>
            ),
          },
          {
            title: t('role') || 'Role',
            field: 'accessRole',
            lookup: roleLookup,
          },
          {
            title: t('data_scope') || 'Data scope',
            field: 'dataScope',
            lookup: scopeLookup,
          },
          {
            title: t('allowed_partners') || 'Allowed partners',
            field: 'allowedPartners',
            render: (row) => (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {(row.allowedPartners || []).map((slug) => (
                  <Chip key={slug} size="small" label={slug} sx={{ mb: 0.5 }} />
                ))}
              </Stack>
            ),
            editComponent: (props) => (
              <Select
                multiple
                fullWidth
                value={props.value || []}
                onChange={(event) => props.onChange(event.target.value)}
                renderValue={(selected) => (selected || []).join(', ')}
              >
                {partnerOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox checked={(props.value || []).includes(option.value)} />
                    <Typography>{option.label}</Typography>
                  </MenuItem>
                ))}
              </Select>
            ),
          },
          {
            title: t('allowed_fleets') || 'Allowed fleets',
            field: 'allowedFleets',
            render: (row) => (
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {(row.allowedFleets || []).map((fleetId) => {
                  const label = fleetOptions.find((option) => option.value === fleetId)?.label || fleetId;
                  return <Chip key={fleetId} size="small" label={label} sx={{ mb: 0.5 }} />;
                })}
              </Stack>
            ),
            editComponent: (props) => (
              <Select
                multiple
                fullWidth
                value={props.value || []}
                onChange={(event) => props.onChange(event.target.value)}
                renderValue={(selected) => (selected || []).join(', ')}
              >
                {fleetOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox checked={(props.value || []).includes(option.value)} />
                    <Typography>{option.label}</Typography>
                  </MenuItem>
                ))}
              </Select>
            ),
          },
        ]}
        data={data}
        options={{
          actionsColumnIndex: -1,
          paging: true,
          pageSize: 10,
          search: true,
          headerStyle: { fontWeight: 700 },
        }}
        editable={canEdit ? {
          onRowUpdate: (newData, oldData) => new Promise((resolve) => {
            const updatedUser = {
              ...oldData,
              ...newData,
              accessRole: newData.accessRole,
              dataScope: newData.dataScope,
              allowedPartners: Array.isArray(newData.allowedPartners) ? newData.allowedPartners : [],
              allowedFleets: Array.isArray(newData.allowedFleets) ? newData.allowedFleets : [],
            };
            delete updatedUser.tableData;
            delete updatedUser.location;
            delete updatedUser.name;
            dispatch(editUser(updatedUser.id, updatedUser));
            resolve();
          }),
        } : {}}
      />
    </Box>
  );
}
